import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AppShell } from "@/components/AppShell";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/dates";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{children}</Label>
);

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const ACTION_STYLE = {
  created: "bg-emerald-50 text-emerald-700",
  edited: "bg-amber-50 text-amber-700",
  deleted: "bg-red-50 text-red-700",
  converted: "bg-sky-50 text-sky-700",
};

const when = (iso) => {
  if (!iso) return "-";
  return fmtDateTime(iso);
};

export default function Activity() {
  const [profile, setProfile] = useState(null);
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [staff, setStaff] = useState("all");
  const [action, setAction] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/activity", {
        params: {
          date_from: from,
          date_to: to,
          staff: staff === "all" ? "" : staff,
          action: action === "all" ? "" : action,
        },
      });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Admin</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">Staff Activity Log</h1>
          <p className="mt-2 text-sm text-zinc-500">Who entered, edited or deleted which bill, and when.</p>
        </div>

        <div className="grid grid-cols-1 gap-4 py-6 sm:grid-cols-2 lg:grid-cols-5 lg:items-end">
          <div>
            <L>From</L>
            <DateInput data-testid="activity-from-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={from} onChange={(v) => setFrom(v)} />
          </div>
          <div>
            <L>To</L>
            <DateInput data-testid="activity-to-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={to} onChange={(v) => setTo(v)} />
          </div>
          <div>
            <L>Staff</L>
            <Select value={staff} onValueChange={setStaff}>
              <SelectTrigger data-testid="activity-staff-select" className="mt-2 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                {(data?.staff || []).map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <L>Action</L>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="activity-action-select" className="mt-2 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                <SelectItem value="created">Created</SelectItem>
                <SelectItem value="edited">Edited</SelectItem>
                <SelectItem value="deleted">Deleted</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="activity-run-button" className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90" onClick={load}>
            Apply filters
          </Button>
        </div>

        {loading ? (
          <p className="py-16 text-center text-sm text-zinc-500">Loading…</p>
        ) : !data || data.entries.length === 0 ? (
          <div data-testid="activity-empty-state" className="border border-dashed border-zinc-300 p-12 text-center">
            <h3 className="font-head text-xl font-bold tracking-tight">Nothing logged here</h3>
            <p className="mt-1 text-sm text-zinc-500">Entries appear as bills and payments are saved.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-zinc-200 bg-white" data-testid="activity-table">
            <table className="w-full text-sm">
              <thead className="border-b border-zinc-200">
                <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">
                  <th className="px-4 py-2 text-left">When</th>
                  <th className="px-4 py-2 text-left">Who</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2 text-left">Action</th>
                  <th className="px-4 py-2 text-left">What</th>
                  <th className="px-4 py-2 text-left">Bill / Ref</th>
                  <th className="px-4 py-2 text-left">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.entries.map((e) => (
                  <tr key={e.id} data-testid={`activity-row-${e.id}`} className="hover:bg-zinc-50">
                    <td className="font-mono-num px-4 py-2 whitespace-nowrap text-zinc-600">{when(e.at)}</td>
                    <td className="px-4 py-2">{e.user_name || e.user_email}</td>
                    <td className="px-4 py-2 uppercase text-zinc-500">{e.role}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] ${ACTION_STYLE[e.action] || "bg-zinc-100 text-zinc-600"}`}>
                        {e.action}
                      </span>
                    </td>
                    <td className="px-4 py-2">{e.entity}</td>
                    <td className="px-4 py-2 font-semibold">{e.ref || "-"}</td>
                    <td className="px-4 py-2 text-zinc-500">{e.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </AppShell>
  );
}
